import React from "react";
import { Link } from "react-router-dom";
import { Building2, Briefcase, BookOpen, MapPin, Package, Calculator, ArrowRight, ShieldCheck, Users, Globe } from "lucide-react";
import ComplexityTiers from "@/components/ComplexityTiers";
import { GRUENDUNG_TIERS, STEUER_TIERS } from "@/data/complexityTiers";

const services = [
  {
    key: "firmenansiedlung",
    icon: MapPin,
    title: "Firmenansiedlungen",
    text: "Die Handels- und Gewerbefreiheit der schweizerischen Bundesverfassung erlaubt es in der Regel allen Personen, auch Ausländern, in der Schweiz ein Gewerbe zu betreiben, ein Unternehmen zu gründen oder sich anzusiedeln. Wir begleiten Sie von der ersten Standortanalyse bis zur operativen Aufnahme – inklusive Domiziladresse, Bewilligungen und Verwaltungsmandat.",
  },
  {
    key: "gruendung",
    icon: Building2,
    title: "Gesellschaftsgründung",
    text: "Vom ersten Konzept bis zum Eintrag im Handelsregister: GmbH, AG, Holding oder Einzelfirma – wir übernehmen das volle Mandat. Wir kennen die typischen Stolperfallen für DACH-Gründer:innen (Wegzugsbesteuerung, Bankeröffnung, Aufenthaltsstatus) und führen Sie sicher hindurch.",
    tiers: GRUENDUNG_TIERS,
    tierIntro: "Drei typische Konstellationen – damit Sie wissen, wo Ihr Fall ungefähr liegt:",
  },
  {
    key: "vr",
    icon: Briefcase,
    title: "Verwaltungsmandate",
    text: "In Zeiten von Corporate Governance und Shareholder Value ist es zunehmend schwieriger geworden, geeignete und unabhängige Verwaltungsräte zu finden. Verwaltungsräte sind in erster Linie Interessenvertreter der Aktionäre und übernehmen treuhänderische Verantwortung. Wir stellen diese Funktion verlässlich – als nomineller VR mit Schweizer Wohnsitz, mit echter fachlicher Tiefe.",
  },
  {
    key: "buchhaltung",
    icon: BookOpen,
    title: "Finanzbuchhaltung",
    text: "Der Aufbau des Rechnungswesens einer Unternehmung ist die Kunst der Balance zwischen Aufwand und Nutzen – Erfüllung der Gesetze, Kontrolle der Zielerreichung, Darstellung der Vermögenssituation. Wir richten Ihre Buchhaltung praxisnah ein, gerne integriert mit AbaNinja, inklusive Jahresabschluss nach OR und MWST-Abrechnung.",
  },
  {
    key: "steuern",
    icon: Calculator,
    title: "Steuerberatung",
    text: "Unser Schwerpunkt: Steueroptimierung im Verhältnis Schweiz ↔ Deutschland. Wir helfen, Steuerlast legal zu reduzieren, Geldwerte gegen behördlichen Zugriff abzusichern, Firmenwerte steueroptimiert auf die nächste Generation zu übertragen und grenzüberschreitende Erbfälle vorausschauend zu planen.",
    tiers: STEUER_TIERS,
    tierIntro: "Drei Komplexitätsstufen – von der reinen Steuererklärung bis zur internationalen Vermögensstruktur:",
  },
  {
    key: "vorrats",
    icon: Package,
    title: "Vorratsgesellschaften",
    text: "Sie schätzen den schnellen Start? Unsere Vorratsgesellschaften sind bereits im Handelsregister eingetragen und haben noch keine Geschäftstätigkeit aufgenommen. Name, Zweck und Sitz lassen sich individuell anpassen. So sparen Sie Wochen an Gründungszeit.",
  },
];

const focusTopics = [
  { Icon: Calculator,   title: "Steuern sparen ggü. Deutschland", desc: "Tiefere Unternehmens- und Kapitalsteuern in der Schweiz, gezielter Einsatz des DBA Deutschland–Schweiz." },
  { Icon: ShieldCheck,  title: "Geldwerte absichern",            desc: "Schutz von Vermögen vor behördlichem Zugriff durch saubere Struktur und Trennung Betriebs- / Privatvermögen." },
  { Icon: Users,        title: "Vererbung & Nachfolge",          desc: "Steueroptimierte Übertragung von Firmenwerten und Vermögen an die nächste Generation – grenzüberschreitend." },
  { Icon: Globe,        title: "Grenzüberschreitend denken",     desc: "Wegzugsbesteuerung, Aufenthaltsstatus, Holding-Struktur – wir orchestrieren das Zusammenspiel DE / CH." },
];

export default function ServicesPage() {
  return (
    <div className="pt-32 pb-24" data-testid="services-page">
      <div className="mbw-container">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <div className="mbw-overline mb-4">Leistungen</div>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-[#1A2433] leading-[1.02] mb-6">
            Alles für Ihren Unternehmensstart <span className="text-[#991B1B]">in der Schweiz.</span>
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Sechs Kernleistungen, ein Team, ein Versprechen: Sie haben einen festen
            Ansprechpartner und erhalten alles aus einer Hand. Bei Firmengründung und
            Steuerberatung finden Sie zusätzlich eine Klassifizierung in <strong>einfach – standard – speziell</strong>,
            damit Sie auf einen Blick einschätzen können, wo Ihr Fall liegt.
          </p>
        </div>

        {/* Worauf wir uns spezialisiert haben */}
        <div className="mb-16 bg-slate-50 border border-slate-200 rounded-2xl p-8 md:p-10" data-testid="services-focus">
          <div className="mbw-overline mb-3">Worauf wir uns spezialisiert haben</div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2433] mb-8 max-w-2xl">
            Vier Themen, bei denen wir für DACH-Kunden den größten Hebel sehen.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {focusTopics.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-slate-200">
                <div className="w-10 h-10 rounded-lg bg-[#1A2433] flex items-center justify-center mb-4">
                  <t.Icon className="w-5 h-5 text-[#DC2626]" />
                </div>
                <div className="font-heading font-bold text-[#1A2433] mb-1.5 leading-snug">{t.title}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Services list */}
        <div className="space-y-6">
          {services.map((s, i) => (
            <div key={s.key} id={s.key} className="mbw-card p-8 md:p-12 grid md:grid-cols-12 gap-6 md:gap-10 items-start scroll-mt-32" data-testid={`service-detail-${s.key}`}>
              <div className="md:col-span-4 lg:col-span-3 flex md:flex-col items-center md:items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-[#1A2433] flex items-center justify-center flex-shrink-0">
                  <s.icon className="w-7 h-7 text-[#DC2626]" />
                </div>
                <div className="md:mt-4 min-w-0">
                  <div className="mbw-overline">0{i + 1}</div>
                  <h2
                    lang="de"
                    className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-[#1A2433] tracking-tight leading-tight break-words hyphens-auto"
                    style={{ hyphens: "auto" }}
                  >
                    {s.title}
                  </h2>
                </div>
              </div>
              <div className="md:col-span-8 lg:col-span-9 min-w-0">
                <p className="text-slate-600 leading-relaxed text-base md:text-lg">{s.text}</p>

                {s.tiers && (
                  <div className="mt-8" data-testid={`service-${s.key}-tiers-block`}>
                    {s.tierIntro && (
                      <p className="text-sm text-slate-500 uppercase tracking-wider font-semibold mb-4">{s.tierIntro}</p>
                    )}
                    <ComplexityTiers
                      service={s.title}
                      tiers={s.tiers}
                      testIdPrefix={`service-${s.key}`}
                    />
                  </div>
                )}

                <div className="flex flex-wrap gap-4 mt-6">
                  <Link to="/rechner" className="inline-flex items-center gap-2 text-[#1A2433] font-medium hover:text-[#991B1B] transition-colors" data-testid={`service-${s.key}-calc-link`}>
                    Preis kalkulieren <ArrowRight className="w-4 h-4" />
                  </Link>
                  {s.tiers && (
                    <Link to="/so-funktionierts" className="inline-flex items-center gap-2 text-[#991B1B] font-medium hover:underline" data-testid={`service-${s.key}-howto-link`}>
                      So läuft die Zusammenarbeit ab <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 bg-[#1A2433] text-white rounded-2xl p-10 md:p-14 text-center">
          <h3 className="font-heading text-3xl md:text-4xl font-bold mb-4">Sie sind sich nicht sicher, wo Ihr Fall liegt?</h3>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">Ein unverbindliches Erstgespräch klärt Ihre Situation und zeigt den passenden Weg auf – einfach, standard oder speziell.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/kontakt" className="btn-primary" data-testid="services-cta-contact">
              Kostenloses Erstgespräch <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/so-funktionierts" className="btn-inverse" data-testid="services-cta-howto">
              So funktioniert's
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
